import React from 'react';

const BRAND_BLUE = '#2C73EB';
const FUTURA = 'Futura, -apple-system, BlinkMacSystemFont, sans-serif';

const DIMENSIONS = [
  {
    key: 'framing',
    label: 'Framing',
    color: '#0385c3',
    description: 'How clearly the decision was defined and scoped.',
  },
  {
    key: 'values',
    label: 'Values',
    color: '#991bb2',
    description: 'How well priorities and success criteria were articulated.',
  },
  {
    key: 'alternatives',
    label: 'Alternatives',
    color: '#53b723',
    description: 'The range and creativity of options considered.',
  },
  {
    key: 'information',
    label: 'Information',
    color: '#ff8210',
    description: 'The quality and relevance of information brought in.',
  },
  {
    key: 'reasoning',
    label: 'Reasoning',
    color: '#d01102',
    description:
      'How options were weighed against values, with honesty about trade-offs.',
  },
  {
    key: 'commitment',
    label: 'Commitment',
    color: '#ffb20d',
    description: 'The concreteness and accountability of next steps.',
  },
];

// Personalized coaching guidance per dimension. Templates receive
// { personaName } so the copy names the client you just coached.
const STRENGTH_NOTES = {
  framing: ({ personaName }) =>
    `You helped ${personaName} name the real decision instead of staying in the feeling.`,
  values: ({ personaName }) =>
    `You surfaced what ${personaName} actually cares about, not just surface preferences.`,
  alternatives: ({ personaName }) =>
    `You pushed ${personaName} past binary thinking into a real set of options.`,
  information: ({ personaName }) =>
    `You guided ${personaName} to separate assumptions from facts.`,
  reasoning: ({ personaName }) =>
    `You helped ${personaName} weigh options honestly, including the trade-offs.`,
  commitment: ({ personaName }) =>
    `You helped ${personaName} turn the decision into concrete, accountable action.`,
};

const IMPROVEMENT_PLAYS = {
  framing: {
    headline: ({ personaName }) =>
      `Help ${personaName} name the decision, not the feeling.`,
    tips: [
      '“What exactly are you deciding? What would and wouldn’t change depending on your choice?”',
      'When they describe symptoms (“I’m stuck”, “I don’t know”), slow down and ask them to state the specific choice in one sentence.',
      'Separate the frame you’re using from alternative frames: “Is this a career decision, an identity decision, or a money decision?”',
    ],
  },
  values: {
    headline: ({ personaName }) =>
      `Surface and rank what matters most to ${personaName}.`,
    tips: [
      '“What has to be true for you to feel good about this decision a year from now?”',
      'Push past the first list. Ask: “Of those, which two matter most if you can’t have all of them?”',
      'Name the trade-off explicitly: “You’re weighing stability against meaning. Which wins when they conflict?”',
    ],
  },
  alternatives: {
    headline: ({ personaName }) =>
      `Break ${personaName} out of binary thinking.`,
    tips: [
      '“What are three more options you haven’t taken seriously yet?”',
      'Ask for a hybrid: “Is there a version that combines the best of both?”',
      '“What would a trusted mentor suggest that isn’t on your list today?”',
    ],
  },
  information: {
    headline: ({ personaName }) =>
      `Help ${personaName} learn what would actually change the answer.`,
    tips: [
      '“What could you learn in the next week that would change your decision?”',
      'Separate assumptions from facts: “What are you treating as certain that’s actually a guess?”',
      '“Who is one specific person you could talk to before deciding?”',
    ],
  },
  reasoning: {
    headline: ({ personaName }) =>
      `Pressure-test ${personaName}’s thinking before they commit.`,
    tips: [
      '“If this option goes really well, what does that look like? If it goes badly, what did we miss?”',
      'Run the decision forward: “Trace this all the way through. Which outcome can you actually live with?”',
      'Name the bias: “What story are you telling yourself that might not be true?”',
    ],
  },
  commitment: {
    headline: ({ personaName }) =>
      `Turn ${personaName}’s decision into action.`,
    tips: [
      '“What is the first thing you will do, by when, and how will you know it’s working?”',
      'Plan for setbacks: “If this gets hard in week two, what’s your move?”',
      'Define success concretely: “What would tell you in 30 days that this was the right call?”',
    ],
  },
};

const safeValue = (v) =>
  typeof v === 'number' && !isNaN(v) && isFinite(v)
    ? Math.max(0, Math.min(1, v))
    : 0;

const bandFor = (score) => {
  if (score >= 0.8) return 'excellent';
  if (score >= 0.6) return 'solid';
  return 'developing';
};

const overallCopyFor = (band, personaName) => {
  switch (band) {
    case 'excellent':
      return `Excellent coaching across the board. Even your weakest link was strong, so ${personaName} got high-quality guidance throughout.`;
    case 'solid':
      return `Solid session. Your weakest link held the score back — the growth areas below show where to focus in your next attempt.`;
    default:
      return `Your weakest dimension defines this score. Zeroing in on that specific area in your next session will move the needle fastest.`;
  }
};

const bannerCopyFor = (band) => {
  switch (band) {
    case 'excellent':
      return 'You demonstrated strong coaching across all six dimensions of decision quality.';
    case 'solid':
      return 'Good effort. Review the growth areas below to sharpen your coaching approach.';
    default:
      return 'Keep practicing. The coaching moves below are specific to the dimensions you have the most room to grow in.';
  }
};

const DIM_BY_KEY = DIMENSIONS.reduce((acc, d) => {
  acc[d.key] = d;
  return acc;
}, {});

const SessionSummaryReport = ({
  dqScore,
  personaName = 'your client',
  hasWon = false,
  onStartNew,
  review = null,
  reviewStatus = 'idle', // 'idle' | 'loading' | 'ready' | 'failed'
}) => {
  if (!dqScore || typeof dqScore !== 'object') {
    return null;
  }

  const dimensions = DIMENSIONS.map((d) => ({
    ...d,
    value: safeValue(dqScore[d.key]),
  }));

  const finalScore = dimensions.reduce(
    (min, d) => Math.min(min, d.value),
    Infinity
  );
  const overall = isFinite(finalScore) ? finalScore : 0;

  const sortedDesc = [...dimensions].sort((a, b) => b.value - a.value);
  const sortedAsc = [...dimensions].sort((a, b) => a.value - b.value);
  const fallbackStrengths = sortedDesc.filter((d) => d.value >= 0.7).slice(0, 2);
  const fallbackGrowthAreas = sortedAsc.slice(0, 2); // always show the two weakest

  const hasLlmReview =
    reviewStatus === 'ready' &&
    review &&
    (Array.isArray(review.strengths) || Array.isArray(review.growthAreas));
  const llmStrengths = hasLlmReview && Array.isArray(review.strengths)
    ? review.strengths.filter((s) => s && DIM_BY_KEY[s.dimension])
    : [];
  const llmGrowthAreas = hasLlmReview && Array.isArray(review.growthAreas)
    ? review.growthAreas.filter((g) => g && DIM_BY_KEY[g.dimension])
    : [];

  const band = bandFor(overall);
  const overallCopy = overallCopyFor(band, personaName);
  const bannerCopy = bannerCopyFor(band);

  const scoreColor =
    band === 'excellent'
      ? '#059669'
      : band === 'solid'
      ? BRAND_BLUE
      : '#B45309';

  const bannerStyles = {
    excellent: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
    },
    solid: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-900',
    },
    developing: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-900',
    },
  }[band];

  return (
    <div className="mt-4 sm:mt-6">
      <div className="w-full h-px bg-gray-200 mb-6 sm:mb-8" />

      <div className="flex flex-col gap-6 sm:gap-8">
        {/* Header */}
        <div>
          <p className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2">
            Your Coaching Report
          </p>
          <h3
            className="text-[20px] sm:text-[26px] text-[#363636] leading-tight"
            style={{ fontFamily: FUTURA, fontWeight: 500 }}
          >
            Final Decision Quality Score
          </h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span
              className="text-[44px] sm:text-[56px] font-bold leading-none tabular-nums"
              style={{ color: scoreColor, fontFamily: FUTURA }}
            >
              {overall.toFixed(2)}
            </span>
            <span className="text-lg sm:text-xl text-gray-400 font-semibold">
              / 1.0
            </span>
          </div>
          <p className="text-[13px] sm:text-[14px] text-[#555] mt-3 leading-relaxed max-w-2xl">
            {overallCopy} This score is your{' '}
            <span className="font-semibold text-[#363636]">weakest link</span>
            , the lowest of your six dimensions, because a decision is only as
            strong as its weakest element.
          </p>

          {hasLlmReview && review.overallFeedback && (
            <div
              className="mt-4 rounded-[6px] border-l-[3px] bg-blue-50/60 border border-blue-100 px-4 py-3"
              style={{ borderLeftColor: BRAND_BLUE }}
            >
              <p
                className="text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] uppercase mb-1"
                style={{ color: BRAND_BLUE }}
              >
                Personalized Review
              </p>
              <p className="text-[13px] sm:text-[14px] text-[#363636] leading-relaxed">
                {review.overallFeedback}
              </p>
            </div>
          )}

          {reviewStatus === 'loading' && (
            <div className="mt-4 flex items-center gap-2 text-[12px] sm:text-[13px] text-gray-500">
              <span
                className="inline-block w-3 h-3 rounded-full border-2 border-gray-300 border-t-transparent animate-spin"
                style={{ borderTopColor: BRAND_BLUE }}
              />
              <span>Analyzing your transcript for personalized feedback...</span>
            </div>
          )}
        </div>

        {/* Dimensions */}
        <div>
          <p className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">
            Breakdown by dimension
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {dimensions.map((metric) => (
              <div key={metric.key} className="flex flex-col gap-1.5">
                <div className="flex justify-between items-baseline">
                  <p className="text-[14px] font-semibold text-[#363636]">
                    {metric.label}
                  </p>
                  <p className="text-[14px] font-semibold text-[#363636] tabular-nums">
                    {metric.value.toFixed(2)}
                  </p>
                </div>
                <div className="relative w-full h-[10px] bg-[rgba(217,217,217,0.44)] rounded-[10px] overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full rounded-[10px] transition-all duration-500"
                    style={{
                      width: `${metric.value * 100}%`,
                      backgroundColor: metric.color,
                    }}
                  />
                </div>
                <p className="text-[12px] text-gray-500 leading-snug">
                  {metric.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* What worked */}
        {(llmStrengths.length > 0 || fallbackStrengths.length > 0) && (
          <div>
            <p
              className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase mb-3"
              style={{ color: BRAND_BLUE }}
            >
              What worked
            </p>
            {llmStrengths.length > 0 ? (
              <div className="flex flex-col gap-3">
                {llmStrengths.map((s, i) => {
                  const dim = DIM_BY_KEY[s.dimension];
                  const val = safeValue(dqScore[s.dimension]);
                  return (
                    <div
                      key={`${s.dimension}-${i}`}
                      className="rounded-[6px] border border-gray-200 bg-white p-4 sm:p-5"
                      style={{ borderLeft: `3px solid ${dim.color}` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[14px] font-semibold text-[#363636]">
                          {dim.label}
                        </p>
                        <p className="text-[13px] text-gray-400 tabular-nums">
                          {val.toFixed(2)}
                        </p>
                      </div>
                      {s.evidence && (
                        <p className="text-[13px] sm:text-[14px] text-[#363636] leading-snug mb-1.5">
                          {s.evidence}
                        </p>
                      )}
                      {s.note && (
                        <p className="text-[13px] text-[#555] leading-relaxed">
                          {s.note}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <ul className="space-y-2.5">
                {fallbackStrengths.map((d) => (
                  <li
                    key={d.key}
                    className="flex items-start gap-3 text-[14px] text-[#363636] leading-relaxed"
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mt-[9px] flex-shrink-0"
                      style={{ backgroundColor: d.color }}
                    />
                    <span>
                      <span className="font-semibold">{d.label}</span>{' '}
                      <span className="text-gray-400 tabular-nums">
                        ({d.value.toFixed(2)})
                      </span>
                      <span className="text-[#555]">
                        {' — '}
                        {STRENGTH_NOTES[d.key]({ personaName })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* What to try next — LLM-specific coaching moves, else static plays */}
        <div>
          <p
            className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase mb-3"
            style={{ color: BRAND_BLUE }}
          >
            What to try next
          </p>
          <div className="flex flex-col gap-4">
            {llmGrowthAreas.length > 0
              ? llmGrowthAreas.map((g, i) => {
                  const dim = DIM_BY_KEY[g.dimension];
                  const val = safeValue(dqScore[g.dimension]);
                  return (
                    <div
                      key={`${g.dimension}-${i}`}
                      className="rounded-[6px] border border-gray-200 bg-white p-4 sm:p-5"
                      style={{ borderLeft: `3px solid ${dim.color}` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[14px] font-semibold text-[#363636]">
                          {dim.label}
                        </p>
                        <p className="text-[13px] text-gray-400 tabular-nums">
                          {val.toFixed(2)}
                        </p>
                      </div>
                      {g.evidence && (
                        <p className="text-[13px] sm:text-[14px] text-[#363636] leading-snug mb-2">
                          {g.evidence}
                        </p>
                      )}
                      {g.whatHappened && (
                        <p className="text-[13px] text-[#555] leading-relaxed mb-3">
                          {g.whatHappened}
                        </p>
                      )}
                      {g.tryInstead && (
                        <div className="mt-1 rounded-[6px] bg-gray-50 border border-gray-200 px-3 py-2 inline-block max-w-full">
                          <p
                            className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500 mb-1"
                          >
                            Try instead
                          </p>
                          <p className="text-[13px] sm:text-[14px] text-[#363636] leading-snug">
                            {g.tryInstead}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              : fallbackGrowthAreas.map((d) => {
                  const play = IMPROVEMENT_PLAYS[d.key];
                  return (
                    <div
                      key={d.key}
                      className="rounded-[6px] border border-gray-200 bg-white p-4 sm:p-5"
                      style={{ borderLeft: `3px solid ${d.color}` }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[14px] font-semibold text-[#363636]">
                          {d.label}
                        </p>
                        <p className="text-[13px] text-gray-400 tabular-nums">
                          {d.value.toFixed(2)}
                        </p>
                      </div>
                      <p className="text-[14px] text-[#363636] leading-snug mb-3">
                        {play.headline({ personaName })}
                      </p>
                      <ul className="space-y-2">
                        {play.tips.map((tip, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-[13px] text-[#555] leading-relaxed"
                          >
                            <span
                              className="inline-block w-1 h-1 rounded-full mt-[8px] flex-shrink-0 bg-gray-400"
                            />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
          </div>
        </div>

        {/* Banner */}
        <div
          className={`p-4 rounded-[6px] border ${bannerStyles.bg} ${bannerStyles.border}`}
        >
          <p
            className={`text-[13px] sm:text-[14px] font-medium leading-relaxed ${bannerStyles.text}`}
          >
            {bannerCopy}
          </p>
        </div>

        {/* CTA */}
        {onStartNew && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={onStartNew}
              className={`px-6 py-3 rounded-[6px] font-semibold text-white transition-colors ${
                hasWon
                  ? 'bg-green-500 hover:bg-green-600'
                  : ''
              }`}
              style={
                !hasWon
                  ? { backgroundColor: BRAND_BLUE, fontFamily: FUTURA }
                  : { fontFamily: FUTURA }
              }
              onMouseEnter={(e) => {
                if (!hasWon) e.currentTarget.style.backgroundColor = '#1e5bb8';
              }}
              onMouseLeave={(e) => {
                if (!hasWon) e.currentTarget.style.backgroundColor = BRAND_BLUE;
              }}
            >
              Start New Session
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionSummaryReport;
